import type { TrainingDiagram } from '@ukinfo/shared';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeToClose, useScrollLock } from '../ui';
import { DiagramPlayer } from './DiagramPlayer';
import { SketchImageButton } from './ShareImageButton';
import { ZoomControls, useDiagramViewport } from './useDiagramViewport';

/**
 * Skizze bildschirmfuellend ansehen (Handy): mit Abspiel-Leiste, wenn die Skizze Pfeile hat,
 * im Querformat gross mit der Leiste daneben. Zoomen und Schwenken wie im Editor
 * (`useDiagramViewport`: Zwei-Finger-Geste, Strg+Mausrad, Zoomknoepfe, Ziehen).
 *
 * Schliessen per Knopf und Escape - Escape gilt nur dem obersten Overlay, ein darunter
 * liegender Dialog (Uebungsdetail, Einheit) bleibt offen. Das Fenster haengt am body,
 * liegt ueber den Dialogen und ist im Druck unsichtbar.
 */
export function DiagramFullscreen({ diagram, title, onClose }: { diagram: TrainingDiagram; title?: string; onClose: () => void }): JSX.Element {
  const viewport = useDiagramViewport({ pitch: diagram.pitch });
  useScrollLock(true);
  useEscapeToClose(true, onClose);

  // Der Fokus geht in die Ansicht und beim Schliessen zurueck zum Knopf, der sie geoeffnet hat.
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  const name = title ? `Skizze im Vollbild: ${title}` : 'Skizze im Vollbild';
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={name} data-testid="diagram-fullscreen" className="fixed inset-0 z-[60] flex flex-col bg-slate-900 print:hidden">
      {/* Am schmalen Handy steht der Zoom in einer eigenen Zeile unter Titel und Schließen, breiter zwischen beiden. */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 bg-white px-3 py-2">
        <h3 className="min-w-[8rem] flex-1 truncate text-sm font-semibold text-slate-900">{title ?? 'Skizze'}</h3>
        <SketchImageButton diagram={diagram} title={title ?? 'Skizze'} className="min-h-11 sm:order-3" />
        <button
          ref={closeRef}
          type="button"
          aria-label="Vollbild schließen"
          onClick={onClose}
          className="flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 sm:order-3"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6.3 5A1 1 0 004.9 6.4L8.6 10l-3.7 3.6a1 1 0 101.4 1.4l3.7-3.6 3.6 3.6a1 1 0 001.4-1.4L11.4 10l3.6-3.6a1 1 0 00-1.4-1.4L10 8.6 6.3 5z" />
          </svg>
          Schließen
        </button>
        <ZoomControls viewport={viewport} className="w-full justify-center sm:order-2 sm:w-auto" />
      </div>
      <div className="min-h-0 flex-1 p-2">
        <DiagramPlayer diagram={diagram} title={title} fullscreen viewport={viewport} />
      </div>
    </div>,
    document.body,
  );
}
